import { FC, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  useScoringStore,
  MAX_ACTIVE_PROFILES,
  v1SourceId,
  v2SourceId,
  parseSourceId,
} from "@/store/scoringStore";
import { getProfileColor } from "@/lib/scoring/components/ScoreChip";
import {
  AiBadge,
  aiFillGradient,
  aiGlow,
} from "@/lib/scoring/components/AiBadge";
import type { ScoringProfile } from "@/lib/scoring/types";
import { getTenantId } from "@/utils/auth";
import {
  useCanManageScoring,
  MANAGE_SCORING_DISABLED_MESSAGE,
} from "@/hooks/useCanManageScoring";
import { useScoringMethodModal } from "@/hooks/useScoringMethodModal";
import { openAiScoringModel } from "@/utils/module-urls";
import { trackEvent } from "@/utils/analytics";
import { getAnalyticsSegmentName } from "@/utils/analyticsContext";

interface ScoringSourcePillsProps {
  /** Create handler — opens the method chooser or deep-links, per the caller. */
  onCreateNew: () => void;
  /** Open the legacy profile editor for an existing V1 profile. */
  onEditProfile: (profile: ScoringProfile) => void;
}

/** Shared style for the picker's section headers (legacy vs AI-assisted).
 * Aligned with the "Create" row (px 2); options sit indented under it (pl 3). */
const SECTION_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 2,
  pt: 1.5,
  pb: 0.25,
  lineHeight: 1.4,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.7,
  textTransform: "uppercase",
  color: "text.primary",
  bgcolor: "transparent",
} as const;

/**
 * Merged scoring-source picker: legacy (niffler) profiles and new (argo) models
 * in one list, sharing a single active budget. Selecting a legacy profile feeds
 * the `scoringProfiles` payload; selecting a model feeds `modelScores`. New-model
 * authoring lives in the admin AI-scoring tool, so "Create" deep-links out.
 *
 * Rendered only when the V2 flag is on; the V1-only matrix keeps using
 * {@link ScoringProfilePills}.
 */
export const ScoringSourcePills: FC<ScoringSourcePillsProps> = ({
  onCreateNew,
  onEditProfile,
}) => {
  const canManage = useCanManageScoring();
  const showMethodModal = useScoringMethodModal();
  const profiles = useScoringStore((s) => s.profiles);
  const models = useScoringStore((s) => s.models);
  const activeSourceIds = useScoringStore((s) => s.activeSourceIds);
  const activateSource = useScoringStore((s) => s.activateSource);
  const deactivateSource = useScoringStore((s) => s.deactivateSource);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const activeSet = new Set(activeSourceIds);
  // Two alphabetically-sorted sections in the picker: legacy profiles and new
  // scoring models, kept apart by section headers.
  const inactiveModels = models
    .filter((m) => !activeSet.has(v2SourceId(m.family)))
    .map((m) => ({
      sid: v2SourceId(m.family),
      name: m.name,
      isModel: true as const,
      profile: undefined,
      modelId: m._id,
      modelFamily: m.family,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const inactiveProfiles = profiles
    .filter((p) => !activeSet.has(v1SourceId(p.id)))
    .map((p) => ({
      sid: v1SourceId(p.id),
      name: p.name,
      isModel: false as const,
      profile: p,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const atMax = activeSourceIds.length >= MAX_ACTIVE_PROFILES;
  const hasAny = activeSourceIds.length > 0;

  const filteredProfiles = useMemo(() => {
    if (!searchQuery) return inactiveProfiles;
    const q = searchQuery.toLowerCase();
    return inactiveProfiles.filter((p) => p.name.toLowerCase().includes(q));
  }, [inactiveProfiles, searchQuery]);

  const filteredModels = useMemo(() => {
    if (!searchQuery) return inactiveModels;
    const q = searchQuery.toLowerCase();
    return inactiveModels.filter((m) => m.name.toLowerCase().includes(q));
  }, [inactiveModels, searchQuery]);

  const showSearch = inactiveProfiles.length + inactiveModels.length > 0;

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSearchQuery("");
  };

  const handleCreate = () => {
    handleCloseMenu();
    onCreateNew();
  };

  const renderActiveChip = (sid: string, index: number) => {
    const parsed = parseSourceId(sid);
    if (parsed?.kind === "v1") {
      const profile = profiles.find((p) => p.id === parsed.profileId);
      if (!profile) return null;
      // Color by position among active V1 profiles only — NOT the global index —
      // so a V1 profile keeps its stable green/blue/purple slot regardless of any
      // V2 models sharing the active list. `getProfileColor` is the same source
      // the grid column header uses, so the pill's fill matches its column.
      const v1Index = activeSourceIds
        .slice(0, index)
        .filter((s) => s.startsWith("v1:")).length;
      const chipColor = getProfileColor(v1Index + 1);
      return (
        <Chip
          key={sid}
          data-testid={`scoring-source-pill-${sid}`}
          onDelete={() => deactivateSource(sid)}
          color={chipColor}
          label={
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {profile.name}
              {canManage && (
                <Box
                  component="span"
                  role="button"
                  aria-label={`Edit ${profile.name}`}
                  data-testid={`scoring-edit-profile-btn-${profile.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProfile(profile);
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              )}
            </Box>
          }
          sx={{
            maxWidth: 300,
            color: "#fff",
            "& .MuiChip-deleteIcon": {
              color: "rgba(255,255,255,0.75)",
              "&:hover": { color: "#fff" },
            },
          }}
        />
      );
    }
    if (parsed?.kind === "v2") {
      const model = models.find((m) => m.family === parsed.family);
      if (!model) return null;
      // Distinct fill per active model (position among active models).
      const v2Index = activeSourceIds
        .slice(0, index)
        .filter((s) => s.startsWith("v2:")).length;
      // Distinct self-gradient fill + colored glow (no border) marks new models.
      return (
        <Chip
          key={sid}
          data-testid={`scoring-source-pill-${sid}`}
          onDelete={() => deactivateSource(sid)}
          label={
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {model.name}
              </Box>
              {canManage && (
                <Box
                  component="span"
                  role="button"
                  aria-label={`Edit ${model.name}`}
                  data-testid={`scoring-edit-model-btn-${model.family}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openAiScoringModel(getTenantId(), model._id);
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": { color: "#fff" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              )}
            </Box>
          }
          sx={{
            maxWidth: 260,
            borderRadius: 999,
            background: aiFillGradient(v2Index),
            color: "#fff",
            boxShadow: aiGlow(v2Index),
            "& .MuiChip-deleteIcon": {
              color: "rgba(255,255,255,0.75)",
              "&:hover": { color: "#fff" },
            },
          }}
        />
      );
    }
    return null;
  };

  const renderOption = (option: {
    sid: string;
    name: string;
    isModel: boolean;
    profile?: ScoringProfile;
    modelId?: string;
    modelFamily?: string;
  }) => (
    <MenuItem
      key={option.sid}
      data-testid={
        option.isModel
          ? `scoring-model-option-${option.sid.slice(3)}`
          : `scoring-profile-option-${option.sid.slice(3)}`
      }
      onClick={() => {
        if (atMax) return;
        activateSource(option.sid);
        if (option.isModel) {
          trackEvent("scoring_profile_applied", {
            model_id: option.modelId,
            model_family: option.modelFamily,
            segment: getAnalyticsSegmentName(),
          });
        }
        handleCloseMenu();
      }}
      // Indented one level under its section header; extra right padding keeps
      // the edit icon off the menu border. Fixed minHeight so profile rows (with
      // an edit button) and model rows (text only) share the same spacing.
      sx={{ gap: 1, pl: 3, pr: 2, minHeight: 40 }}
    >
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          color: atMax ? "text.disabled" : "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option.name}
      </Box>
      {option.profile && canManage && (
        <Tooltip title="Edit profile">
          <IconButton
            size="small"
            aria-label={`Edit ${option.profile.name}`}
            data-testid={`scoring-edit-profile-btn-${option.profile.id}`}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseMenu();
              if (option.profile) onEditProfile(option.profile);
            }}
            // Negative vertical margin so the button doesn't stretch the row
            // past its minHeight (keeps V1 rows level with V2 rows).
            sx={{ ml: "auto", p: 0.5, my: -0.5 }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {option.isModel && option.modelId && canManage && (
        <Tooltip title="Edit in AI scoring">
          <IconButton
            size="small"
            aria-label={`Edit ${option.name}`}
            data-testid={`scoring-edit-model-btn-${option.sid.slice(3)}`}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseMenu();
              openAiScoringModel(getTenantId(), option.modelId ?? null);
            }}
            sx={{ ml: "auto", p: 0.5, my: -0.5 }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </MenuItem>
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      {activeSourceIds.map((sid, index) => renderActiveChip(sid, index))}

      {!hasAny ? (
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<AutoAwesomeIcon />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          data-testid="scoring-add-source-btn"
          sx={{ textTransform: "none", flexShrink: 0, fontWeight: 600 }}
        >
          Add scoring
        </Button>
      ) : (
        !atMax && (
          <Tooltip title="Add another scoring profile or model">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              data-testid="scoring-add-source-btn"
              sx={{ flexShrink: 0 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: { sx: { maxHeight: 360, minWidth: 240, maxWidth: 300 } },
        }}
      >
        <Tooltip
          title={canManage ? "" : MANAGE_SCORING_DISABLED_MESSAGE}
          placement="right"
        >
          <span>
            <MenuItem
              data-testid="scoring-create-profile-btn"
              disabled={!canManage}
              onClick={handleCreate}
              sx={{ gap: 0.75, py: 1, color: "primary.main", fontWeight: 600 }}
            >
              <AddIcon fontSize="small" />
              Create scoring profile
              {!showMethodModal && <OpenInNewIcon sx={{ fontSize: 14 }} />}
            </MenuItem>
          </span>
        </Tooltip>

        {showSearch && [
          <Divider key="search-divider" sx={{ my: 0.5 }} />,
          <Box key="search-box" sx={{ px: 1.5, py: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search scoring..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus
              data-testid="scoring-search-input"
              inputProps={{ "data-testid": "scoring-search-input-field" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: 14 } }}
            />
          </Box>,
        ]}

        {filteredProfiles.length > 0 && [
          <ListSubheader key="profiles-header" disableSticky sx={SECTION_SX}>
            Basic
          </ListSubheader>,
          ...filteredProfiles.map(renderOption),
        ]}

        {filteredModels.length > 0 && [
          <ListSubheader key="models-header" disableSticky sx={SECTION_SX}>
            Advanced
            <Box component="span" sx={{ ml: "3px", display: "inline-flex" }}>
              <AiBadge />
            </Box>
          </ListSubheader>,
          ...filteredModels.map(renderOption),
        ]}

        {searchQuery &&
          filteredProfiles.length === 0 &&
          filteredModels.length === 0 && (
            <MenuItem
              disabled
              sx={{
                fontSize: 14,
                color: "text.secondary",
                justifyContent: "center",
              }}
            >
              No results found
            </MenuItem>
          )}
      </Menu>
    </Stack>
  );
};
